import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Tag, Calendar, Users, BedDouble, CreditCard,
  AlertCircle, Clock, User, FileText, Phone, MapPin, CheckCircle,
  XCircle, Clock3, CalendarCheck, CalendarX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import apiClient from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

const STATUS_STYLES = {
  Hold:       { cls: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock3 },
  Confirmed:  { cls: 'bg-green-100 text-green-800 border-green-200',   icon: CheckCircle },
  Cancelled:  { cls: 'bg-red-100 text-red-800 border-red-200',         icon: XCircle },
  CheckedIn:  { cls: 'bg-blue-100 text-blue-800 border-blue-200',      icon: CheckCircle },
  CheckedOut: { cls: 'bg-gray-100 text-gray-800 border-gray-200',      icon: CheckCircle },
  NoShow:     { cls: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle },
};

const toUtcDate = (str) => {
  if (!str) return new Date(0);
  return new Date(str.endsWith('Z') ? str : str + 'Z');
};

const CountdownTimer = ({ expiresAt }) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = toUtcDate(expiresAt) - new Date();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return <span className="font-mono font-medium">{remaining}</span>;
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 flex-shrink-0 mr-4">{label}</span>
    <span className="text-sm font-medium text-gray-800 text-right">{value}</span>
  </div>
);

const BookingDetailPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login', { state: { from: `/my-bookings/${bookingId}` } }); }
  }, [isLoggedIn, navigate, bookingId]);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get(`/api/Book/Detail/${bookingId}`);
      setBooking(data);
    } catch (err) {
      setError(err.message || 'Failed to load booking details.');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (isLoggedIn) fetchDetail();
  }, [isLoggedIn, fetchDetail]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setCancelling(true);
    setCancelError('');
    try {
      await apiClient.post(`/api/Book/Cancel/${bookingId}`);
      setBooking(b => ({ ...b, status: 'Cancelled' }));
    } catch (err) {
      setCancelError(err.message || 'Cancellation failed.');
    } finally {
      setCancelling(false);
    }
  };

  if (!isLoggedIn) return null;

  const statusMeta = booking ? (STATUS_STYLES[booking.status] || STATUS_STYLES.Hold) : null;
  const StatusIcon = statusMeta?.icon;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-10">
      <div className="container max-w-3xl mx-auto px-4">

        {/* Back */}
        <button
          onClick={() => navigate('/my-bookings')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm mb-6 transition-colors"
        >
          <ArrowLeft size={16} /> Back to My Bookings
        </button>

        {loading && (
          <div className="flex justify-center py-24">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16">
            <AlertCircle className="mx-auto mb-3 text-red-400" size={40} />
            <p className="text-gray-600">{error}</p>
            <Button className="mt-4" onClick={fetchDetail}>Retry</Button>
          </div>
        )}

        {!loading && booking && (
          <div className="space-y-5">

            {/* Header Card */}
            <Card className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl font-bold text-gray-900">{booking.roomTypeName}</h1>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${statusMeta?.cls}`}>
                      {StatusIcon && <StatusIcon size={12} />}
                      {booking.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Tag size={14} className="text-blue-500" />
                    <span className="font-mono font-semibold text-gray-700 text-lg">{booking.pnr}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-600">৳{Number(booking.grandTotal).toLocaleString()}</p>
                  <p className="text-sm text-gray-500">{booking.currency} · {booking.paymentStatus}</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                  <CalendarCheck size={13} className="text-green-500" />
                  Booked: {toUtcDate(booking.bookedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
                {booking.cancelledAt && (
                  <span className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                    <CalendarX size={13} />
                    Cancelled: {toUtcDate(booking.cancelledAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>

              {booking.status === 'Hold' && booking.holdExpiresAt && (
                <div className="mt-3 flex items-center gap-2 text-sm bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2.5 text-yellow-700">
                  <Clock size={15} />
                  Hold expires in: <CountdownTimer expiresAt={booking.holdExpiresAt} />
                </div>
              )}

              {cancelError && (
                <div className="mt-3 flex items-center gap-2 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 text-red-700">
                  <AlertCircle size={15} /> {cancelError}
                </div>
              )}
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Stay Details */}
              <Card className="p-5">
                <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar size={16} className="text-blue-500" /> Stay Details
                </h2>
                <Row label="Check-in" value={toUtcDate(booking.checkInDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
                <Row label="Check-out" value={toUtcDate(booking.checkOutDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
                <Row label="Nights" value={booking.nights} />
                <Row
                  label="Rooms"
                  value={
                    <span className="flex items-center gap-1">
                      <BedDouble size={13} className="text-blue-400" />
                      {booking.numberOfRooms} room{booking.numberOfRooms !== 1 ? 's' : ''}
                    </span>
                  }
                />
                <Row
                  label="Guests"
                  value={
                    <span className="flex items-center gap-1">
                      <Users size={13} className="text-blue-400" />
                      {booking.adults} adult{booking.adults !== 1 ? 's' : ''}
                      {booking.children > 0 ? `, ${booking.children} child${booking.children !== 1 ? 'ren' : ''}` : ''}
                    </span>
                  }
                />
              </Card>

              {/* Price Breakdown */}
              <Card className="p-5">
                <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <CreditCard size={16} className="text-blue-500" /> Price Breakdown
                </h2>
                <Row label="Sub-total" value={`৳${Number(booking.subTotal).toLocaleString()}`} />
                <Row label="Tax" value={`৳${Number(booking.taxAmount).toLocaleString()}`} />
                {booking.serviceChargeAmount > 0 && (
                  <Row label="Service Charge" value={`৳${Number(booking.serviceChargeAmount).toLocaleString()}`} />
                )}
                <div className="flex justify-between items-center pt-3 mt-1 border-t border-gray-200">
                  <span className="text-sm font-bold text-gray-800">Grand Total</span>
                  <span className="text-lg font-bold text-blue-600">৳{Number(booking.grandTotal).toLocaleString()}</span>
                </div>
              </Card>
            </div>

            {/* Lead Guest Details */}
            {booking.leadPassenger && (
              <Card className="p-5">
                <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <User size={16} className="text-blue-500" /> Lead Guest
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <Row label="Name" value={`${booking.leadPassenger.firstName} ${booking.leadPassenger.lastName}`} />
                  {booking.leadPassenger.email && (
                    <Row label="Email" value={booking.leadPassenger.email} />
                  )}
                  {booking.leadPassenger.phone && (
                    <Row
                      label="Phone"
                      value={
                        <span className="flex items-center gap-1">
                          <Phone size={12} className="text-gray-400" /> {booking.leadPassenger.phone}
                        </span>
                      }
                    />
                  )}
                  {(booking.leadPassenger.address || booking.leadPassenger.city) && (
                    <Row
                      label="Address"
                      value={
                        <span className="flex items-center gap-1">
                          <MapPin size={12} className="text-gray-400" />
                          {[booking.leadPassenger.address, booking.leadPassenger.city].filter(Boolean).join(', ')}
                        </span>
                      }
                    />
                  )}
                  {booking.leadPassenger.documentType && (
                    <Row
                      label="Document"
                      value={
                        <span className="flex items-center gap-1">
                          <FileText size={12} className="text-gray-400" />
                          {booking.leadPassenger.documentType}: {booking.leadPassenger.documentNumber}
                        </span>
                      }
                    />
                  )}
                </div>
              </Card>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pb-4">
              <Button variant="outline" onClick={() => navigate('/my-bookings')}>
                Back to Bookings
              </Button>
              {(booking.status === 'Hold' || booking.status === 'Confirmed') && (
                <Button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Booking'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetailPage;
